import { useMemo } from 'react';

import { decodeBrief } from '../brief/request';
import { flossByCode } from '../data/dmc';
import { generateCandidates } from '../engine/generate';
import { encodeDesign } from '../engine/serialize';
import { buildGuide, formatMinutes } from '../engine/guide';
import { BraceletPreview, PaletteStrip } from './Preview';
import { hrefFor } from './router';
import type { Settings } from '../state/storage';

interface Props {
  briefCode: string;
  seed: string;
  settings: Settings;
}

export function OptionsScreen({ briefCode, seed, settings }: Props) {
  const brief = useMemo(() => decodeBrief(briefCode), [briefCode]);

  const candidates = useMemo(() => {
    if (!brief) return [];
    return generateCandidates(brief, seed, settings.gauge).map((c) => ({
      ...c,
      code: encodeDesign(c.design),
      guide: buildGuide(c.design, settings.gauge),
    }));
  }, [brief, seed, settings.gauge]);

  if (!brief) {
    return (
      <div className="screen">
        <section className="card">
          <h2>That link did not make sense</h2>
          <p>
            The description in the address bar could not be read. Nothing is lost — start again and
            it will work.
          </p>
          <a className="primary as-button" href="#/">
            Start again
          </a>
        </section>
      </div>
    );
  }

  return (
    <div className="screen">
      <section className="card card--tight">
        <h2>{candidates.length} designs for “{brief.text || 'your bracelet'}”</h2>
        <p className="muted small">{brief.sourceNote}</p>
        {brief.source !== 'gemini' && (
          <p className="badge badge--plain">
            {brief.source === 'photo' ? 'From your photo, no AI' : 'Worked out on this device, no AI'}
          </p>
        )}
        {brief.source === 'gemini' && <p className="badge">Colours suggested by Gemini · knots by Knotwright</p>}
        <PaletteStrip palette={brief.colors.map(paletteEntry)} compact />
        <p className="muted small">
          Seed <code>{seed || 'default'}</code> · same seed, same designs, every time.
        </p>
      </section>

      {candidates.map((c) => (
        <a className="card option" key={c.code} href={hrefFor({ name: 'design', code: c.code })}>
          <div className="option-preview">
            <BraceletPreview design={c.design} height={78} />
          </div>
          <div className="option-body">
            <h3>{c.design.name}</h3>
            <p>{c.design.blurb}</p>
            <p className="muted small">{c.rationale}</p>
            <ul className="facts">
              <li>
                <strong>{styleName(c.design.body.style)}</strong>
              </li>
              <li>{c.guide.totalKnots.toLocaleString()} knots</li>
              <li>~{formatMinutes(c.guide.estimate.minutes)}</li>
              <li>{c.guide.estimate.widthMm.toFixed(0)} mm wide</li>
            </ul>
            <PaletteStrip palette={c.design.palette} compact />
          </div>
        </a>
      ))}

      <section className="card">
        <a className="link" href="#/">
          ← Change the description, colours or seed
        </a>
      </section>
    </div>
  );
}

function styleName(style: string): string {
  if (style === 'normal') return 'Diagonal knots';
  if (style === 'alpha') return 'Alpha (pixel grid)';
  return 'Motif chain';
}

function paletteEntry(code: string) {
  const f = flossByCode(code);
  return { dmc: code, name: f?.name ?? 'Unknown', hex: f?.hex ?? '#999999' };
}
